/* eslint-disable complexity, max-statements */
import { memo, useMemo } from 'react';

import { InlineSkeleton } from '@mindtickle/skeleton';

import { useNavigationConfig } from '~/components/Navigation/hooks/useNavigationConfig';
import type { IntlType, NavRouteType } from '~/components/Navigation/types';
import toHyphenatedString from '~/components/Navigation/utils/toHyphenatedString';

import { getFormattedName } from '../../../../utils/getFormattedName';
import NavRouteItem from '../../../NavMenuItem';
import { loaderStyle, StyledNavLoaderItem } from './styles';

type SubMenuListProps = {
  menuId: string;
  subMenu: NavRouteType[];
  intl: IntlType;
  activeRouteId: string;
  routeItemName?: string;
  isLoading: boolean;
  isSubMenuVisible: Record<string, boolean>;
};

type RenderSubMenuItemArgs = {
  item: NavRouteType;
  intl: IntlType;
  menuId: string;
  activeRouteId: string;
  routeItemName?: string;
  isLoading: boolean;
  isSubMenuVisible: Record<string, boolean>;
  subMenuMessages: Record<string, any>;
};

const renderSubMenuItem = ({
  item,
  intl,
  menuId,
  activeRouteId,
  routeItemName,
  isLoading,
  isSubMenuVisible,
  subMenuMessages,
}: RenderSubMenuItemArgs) => {
  const { link, id, showAfterPermissionsCheck } = item;
  if (!id) return null;

  const isGated = Boolean(showAfterPermissionsCheck);
  const isAllowed = isSubMenuVisible[id] ?? !isGated;

  if (isGated && isLoading) {
    return (
      <StyledNavLoaderItem
        key={id}
        className={`${routeItemName} nav-submenu-loader`}
        data-testid={`submenu-loader-${toHyphenatedString({
          str: id,
        })}`}
      >
        <InlineSkeleton style={loaderStyle} />
      </StyledNavLoaderItem>
    );
  }

  if (isGated && !isAllowed) {
    return null;
  }

  const messageConfig = subMenuMessages[id];

  return (
    <NavRouteItem
      dataTestId={`submenu-${toHyphenatedString({
        str: id,
      })}`}
      key={id}
      link={link}
      className={routeItemName}
      isActive={activeRouteId === id}
      name={getFormattedName({
        intl,
        menuId: id ?? menuId,
        messageConfig,
      })}
    />
  );
};

type BuildSubMenuItemsArgs = {
  menuId: string;
  subMenu: NavRouteType[];
  intl: IntlType;
  activeRouteId: string;
  routeItemName?: string;
  isLoading: boolean;
  isSubMenuVisible: Record<string, boolean>;
  menuConfig: any;
};

const buildSubMenuItems = ({
  menuId,
  subMenu,
  intl,
  activeRouteId,
  routeItemName,
  isLoading,
  isSubMenuVisible,
  menuConfig,
}: BuildSubMenuItemsArgs) => {
  if (!menuConfig) return null;

  const subMenuMessages = menuConfig.subMenuName;

  return subMenu
    .filter(({ id }) => id && subMenuMessages[id])
    .map(item =>
      renderSubMenuItem({
        item,
        intl,
        menuId,
        activeRouteId,
        routeItemName,
        isLoading,
        isSubMenuVisible,
        subMenuMessages,
      })
    );
};

const SubMenuList = ({
  menuId,
  subMenu,
  intl,
  activeRouteId,
  routeItemName,
  isLoading,
  isSubMenuVisible,
}: SubMenuListProps) => {
  const { navigationConfig } = useNavigationConfig();
  const menuConfig = navigationConfig[menuId];

  const items = useMemo(
    () =>
      buildSubMenuItems({
        menuId,
        subMenu,
        intl,
        activeRouteId,
        routeItemName,
        isLoading,
        isSubMenuVisible,
        menuConfig,
      }),
    [activeRouteId, intl, isLoading, isSubMenuVisible, menuConfig, menuId, routeItemName, subMenu]
  );

  return <>{items}</>;
};

export default memo(SubMenuList);
